import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'

import { apiFetch, formatApiError } from '../shared/api/client'
import { authSchema } from '../shared/api/schemas'

type AuthFormValues = {
  email: string
  password: string
  username?: string
}

export function AuthModal({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<'login' | 'register'>('login')

  const form = useForm<AuthFormValues>({
    resolver: zodResolver(authSchema),
    defaultValues: { email: '', password: '', username: '' },
    shouldUnregister: true,
  })

  const mutation = useMutation({
    mutationFn: (values: AuthFormValues) =>
      apiFetch(mode === 'register' ? '/auth/register' : '/auth/login', {
        method: 'POST',
        body: JSON.stringify(
          mode === 'register'
            ? values
            : { email: values.email, password: values.password },
        ),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth-status'] })
      form.reset({ email: '', password: '', username: '' })
      onClose()
      onSuccess?.()
    },
  })

  useEffect(() => {
    if (!isOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const inputClassName = (hasError: boolean) =>
    `mt-2 w-full rounded-2xl border bg-slate-50 px-4 py-3 outline-none transition focus:bg-white focus:ring-4 ${
      hasError
        ? 'border-rose-400 focus:border-rose-400 focus:ring-rose-500/10'
        : 'border-slate-200 focus:border-emerald-400 focus:ring-emerald-500/10'
    }`

  return (
    <div
      className="animate-fade-in fixed inset-0 z-50 flex items-end justify-center bg-slate-950/45 p-0 backdrop-blur-sm md:items-center md:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
      onClick={onClose}
    >
      <div
        className="animate-slide-up flex max-h-[90dvh] w-full max-w-md flex-col overflow-y-auto rounded-t-[28px] bg-white p-0 pb-safe shadow-2xl ring-1 ring-slate-200 md:animate-none md:rounded-[28px]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex w-full justify-center pt-3 md:hidden">
          <div className="h-1.5 w-12 rounded-full bg-slate-200" />
        </div>

        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">
              Gatekeeping
            </p>
            <h2 id="auth-modal-title" className="mt-1 text-xl font-semibold tracking-tight text-slate-900">
              {mode === 'login' ? 'Prijava' : 'Registracija'}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label="Zapri prijavo"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 md:p-6">
          <div className="mb-6 flex gap-2 rounded-2xl bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => setMode('login')}
              className={`flex-1 rounded-xl px-4 py-2 text-sm font-semibold transition ${
                mode === 'login'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Prijava
            </button>
            <button
              type="button"
              onClick={() => setMode('register')}
              className={`flex-1 rounded-xl px-4 py-2 text-sm font-semibold transition ${
                mode === 'register'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Nov račun
            </button>
          </div>

          <form
            className="space-y-4"
            onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
          >
            {mode === 'register' ? (
              <label className="block text-sm font-medium text-slate-700">
                Uporabniško ime
                <input
                  {...form.register('username')}
                  aria-invalid={Boolean(form.formState.errors.username)}
                  placeholder="piknikar"
                  className={inputClassName(Boolean(form.formState.errors.username))}
                />
                {form.formState.errors.username ? (
                  <p className="mt-1 text-xs text-rose-500">
                    {form.formState.errors.username.message}
                  </p>
                ) : null}
              </label>
            ) : null}

            <label className="block text-sm font-medium text-slate-700">
              Email
              <input
                {...form.register('email')}
                aria-invalid={Boolean(form.formState.errors.email)}
                type="email"
                placeholder="mark@example.com"
                className={inputClassName(Boolean(form.formState.errors.email))}
              />
              {form.formState.errors.email ? (
                <p className="mt-1 text-xs text-rose-500">
                  {form.formState.errors.email.message}
                </p>
              ) : null}
            </label>

            <label className="block text-sm font-medium text-slate-700">
              Geslo
              <input
                {...form.register('password')}
                aria-invalid={Boolean(form.formState.errors.password)}
                type="password"
                placeholder="secret123"
                className={inputClassName(Boolean(form.formState.errors.password))}
              />
              {form.formState.errors.password ? (
                <p className="mt-1 text-xs text-rose-500">
                  {form.formState.errors.password.message}
                </p>
              ) : null}
            </label>

            {mutation.error ? (
              <p className="text-sm text-rose-600">{formatApiError(mutation.error)}</p>
            ) : null}

            <button
              type="submit"
              disabled={mutation.isPending}
              className="w-full rounded-full bg-emerald-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mutation.isPending
                ? 'Nalagam...'
                : mode === 'register'
                  ? 'Ustvari račun'
                  : 'Prijavi se'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
